import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../shared/api';
import { useAuthStore } from './store';
import type { User } from '../../shared/types';

interface LoginResponse {
  accessToken: string;
  refreshToken: string;
  user: User;
}

export function useLogin() {
  const setSession = useAuthStore((s) => s.setSession);
  return useMutation({
    mutationFn: async (creds: { username: string; password: string }) => {
      const { data } = await api.post<LoginResponse>('/auth/login', creds);
      return data;
    },
    onSuccess: (data) => setSession(data),
  });
}

export function useLogout() {
  const logout = useAuthStore((s) => s.logout);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      try {
        await api.post('/auth/logout');
      } catch {}
    },
    onSettled: () => {
      logout();
      qc.clear();
    },
  });
}

export function useChangePassword() {
  const setUser = useAuthStore((s) => s.setUser);
  const user = useAuthStore((s) => s.user);
  return useMutation({
    mutationFn: async (data: { oldPassword: string; newPassword: string }) => {
      const { data: result } = await api.post('/auth/change-password', data);
      return result;
    },
    onSuccess: () => {
      if (user) setUser({ ...user, mustResetPassword: false });
    },
  });
}

export function useMe() {
  const token = useAuthStore((s) => s.accessToken);
  return useQuery({
    queryKey: ['me'],
    queryFn: async () => {
      const { data } = await api.get<User>('/auth/me');
      return data;
    },
    enabled: !!token,
  });
}
